/**
 * A password field you can look at.
 *
 * People mistype passwords far more often than they are shoulder-surfed, and a masked field with
 * no way to check it is the reason "wrong password" is the most common error on any sign-in
 * screen. The toggle is a button rather than a checkbox so it can sit inside the field, and it
 * says which state it will move to, not which state it is in.
 *
 * It never changes the field's `name` or value — this is a display concern only, and a manager
 * copying a password out of a browser should get exactly what they typed.
 */

import { useState, type ChangeEvent } from "react";
import { IconEye, IconEyeOff } from "./icons";
import { Input } from "./ui";

export function PasswordInput({
  id,
  name = "password",
  autoComplete,
  required,
  minLength,
  placeholder,
  defaultValue,
  value,
  onChange,
}: {
  id: string;
  name?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  placeholder?: string;
  defaultValue?: string;
  /**
   * Controlled, for a form that outlives the field.
   *
   * A two-step sign-up unmounts step one when it moves on; uncontrolled, what was typed would be
   * gone on the way back. Pass both or neither.
   */
  value?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  const [shown, setShown] = useState(false);

  return (
    <div className="relative">
      <Input
        id={id}
        name={name}
        type={shown ? "text" : "password"}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        placeholder={placeholder}
        {...(onChange ? { value: value ?? "", onChange } : { defaultValue })}
        className="pr-11"
      />
      <button
        type="button"
        onClick={() => setShown((current) => !current)}
        aria-label={shown ? "Hide password" : "Show password"}
        aria-pressed={shown}
        data-no-ripple
        className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-[var(--radius-control)] p-2 text-ink-400 hover:text-ink-700"
      >
        {shown ? <IconEyeOff size={17} /> : <IconEye size={17} />}
      </button>
    </div>
  );
}
